<template>
  <section
    id="news"
    ref="revealTarget"
    class="section news-section home-scroll-reveal"
    :class="{ 'is-reveal-ready': isRevealReady, 'is-revealed': isRevealed }"
  >
    <div class="page-container">
      <div class="section-heading-row">
        <div class="section-head">
          <span class="section-kicker">NEWS &amp; ANNOUNCEMENTS</span>
          <h2>权威要闻与公告</h2>
        </div>
        <nuxt-link v-if="newsPath" class="section-more" :to="newsPath">
          查看更多
          <i class="i-custom-enter more-link-icon-desktop" aria-hidden="true"></i>
          <i class="i-lucide-chevron-right more-link-icon-mobile" aria-hidden="true"></i>
        </nuxt-link>
      </div>

      <div v-if="hasNews" class="news-grid" :class="{ 'has-latest': latestNews.length > 0 }">
        <div class="news-feature-column">
          <div
            v-if="leadNews"
            class="featured-news"
            @mouseenter="pauseFeatureRotation"
            @mouseleave="resumeFeatureRotation"
          >
            <Transition name="featured-slide" mode="out-in">
              <nuxt-link :key="leadNews.id" class="featured-link" :to="articlePath(leadNews)">
                <div class="featured-media">
                  <home-media
                    :source="leadNews.cover_url"
                    fallback-source="/images/company/profile-video-cover.webp"
                    :alt="leadNews.title"
                    loading="lazy"
                  />
                </div>
                <div class="featured-copy">
                  <h3>{{ leadNews.title }}</h3>
                </div>
              </nuxt-link>
            </Transition>
            <div v-if="featuredNews.length > 1" class="featured-indicators">
              <button
                v-for="(_, index) in featuredNews"
                :key="index"
                class="featured-indicator"
                type="button"
                :class="{ active: index === activeFeatureIndex }"
                :aria-label="`查看第 ${index + 1} 条焦点新闻`"
                @click="selectFeature(index)"
              ></button>
            </div>
          </div>
          <div v-else class="featured-empty">暂无焦点内容</div>
        </div>

        <div class="news-brief-column">
          <template v-if="supportingNews.length > 0">
            <nuxt-link
              v-for="article in supportingNews"
              :key="article.id"
              class="brief-card"
              :to="articlePath(article)"
            >
              <div class="brief-card-copy">
                <h3>{{ article.title }}</h3>
                <p v-if="article.summary || article.subtitle">
                  {{ article.summary || article.subtitle }}
                </p>
              </div>
              <div class="brief-card-footer">
                <time :datetime="article.publish_date">
                  <span>{{ dateParts(article.publish_date).day }}</span>
                  <span>{{ dateParts(article.publish_date).yearMonth }}</span>
                </time>
                <i class="i-custom-enter" aria-hidden="true"></i>
              </div>
            </nuxt-link>
          </template>
          <div v-else class="news-list-empty">暂无新闻简讯</div>
        </div>

        <aside
          v-if="latestNews.length > 0"
          class="news-latest-column"
          :class="{ 'latest-count-two': latestNews.length === 2 }"
        >
          <nuxt-link
            v-for="article in latestNews"
            :key="article.id"
            class="brief-card"
            :to="articlePath(article)"
          >
            <div class="brief-card-copy">
              <h3>{{ article.title }}</h3>
              <p v-if="article.summary || article.subtitle">
                {{ article.summary || article.subtitle }}
              </p>
            </div>
            <div class="brief-card-footer">
              <time :datetime="article.publish_date">
                <span>{{ dateParts(article.publish_date).day }}</span>
                <span>{{ dateParts(article.publish_date).yearMonth }}</span>
              </time>
              <i class="i-custom-enter" aria-hidden="true"></i>
            </div>
          </nuxt-link>
        </aside>
      </div>
      <div v-else class="news-empty">暂无动态内容</div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import type { ArticleItem, HomepageData, HomepageNews } from '~/types/site-api'

  import {
    findArticleCategory,
    getArticleCategoryEntryRoute,
    getArticlePath,
  } from '~/utils/content'

  defineOptions({
    name: 'HomeNews',
  })

  const props = withDefaults(
    defineProps<{
      news?: HomepageData['news']
    }>(),
    {
      news: () => [],
    }
  )

  const {
    targetRef: revealTarget,
    isReady: isRevealReady,
    isVisible: isRevealed,
  } = useScrollReveal(0.12)

  const activeFeatureIndex = shallowRef(0)
  let featureTimer: ReturnType<typeof setInterval> | undefined

  const newsItems = computed(() => {
    const source = normalizeNews(props.news)
    const seen = new Set<string>()
    return source
      .filter((article) => {
        if (seen.has(article.id)) return false
        seen.add(article.id)
        return true
      })
      .slice(0, 5)
  })

  const { data: categoryResponse } = useArticleCategories()
  const categoryTree = computed(() => categoryResponse.value?.data ?? [])
  const newsPath = computed(() => {
    const code = newsItems.value[0]?.category.code
    const match = code ? findArticleCategory(categoryTree.value, code) : undefined
    return match ? getArticleCategoryEntryRoute(match.root).path : undefined
  })

  const featuredNews = computed(() => newsItems.value)

  const leadNews = computed(
    () => featuredNews.value[activeFeatureIndex.value] ?? featuredNews.value[0]
  )

  const supportingNews = computed(() => {
    const leadId = leadNews.value?.id
    return featuredNews.value.filter((article) => article.id !== leadId).slice(0, 2)
  })

  const latestNews = computed(() => {
    const leadId = leadNews.value?.id
    const supportingIds = new Set(supportingNews.value.map((article) => article.id))
    return featuredNews.value.filter(
      (article) => article.id !== leadId && !supportingIds.has(article.id)
    )
  })
  const hasNews = computed(() => newsItems.value.length > 0)

  function pauseFeatureRotation() {
    if (!featureTimer) return
    clearInterval(featureTimer)
    featureTimer = undefined
  }

  function resumeFeatureRotation() {
    if (
      typeof window === 'undefined' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      featuredNews.value.length < 2 ||
      featureTimer
    ) {
      return
    }

    featureTimer = setInterval(() => {
      activeFeatureIndex.value = (activeFeatureIndex.value + 1) % featuredNews.value.length
    }, 4800)
  }

  function selectFeature(index: number) {
    activeFeatureIndex.value = index
  }

  watch(featuredNews, () => {
    activeFeatureIndex.value = 0
    pauseFeatureRotation()
    resumeFeatureRotation()
  })

  onMounted(resumeFeatureRotation)
  onUnmounted(pauseFeatureRotation)

  function normalizeNews(news: HomepageNews) {
    if (Array.isArray(news)) return news
    return [...news.featured, ...news.brief, ...news.latest]
  }

  function dateParts(date: string) {
    const [year = '-', month = '', day = ''] = date.slice(0, 10).split('-')
    return {
      day: day || '-',
      yearMonth: year !== '-' && month ? `${year}.${month}` : year,
    }
  }

  function articlePath(article: ArticleItem) {
    return getArticlePath(article)
  }
</script>

<style scoped lang="scss">
  .news-section {
    background: var(--surface-soft);
  }

  .section-heading-row {
    display: flex;
    gap: 32px;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 48px;
  }

  .section-head {
    min-width: 0;
    margin-bottom: 0;
  }

  .section-kicker {
    display: inline-flex;
    gap: 10px;
    align-items: center;
    margin-bottom: 12px;
    font:
      650 12px/1.4 ui-monospace,
      SFMono-Regular,
      Menlo,
      monospace;
    color: var(--accent);
    letter-spacing: 0;
  }

  .section-kicker::before {
    width: 28px;
    height: 2px;
    content: '';
    background: currentColor;
  }

  .section-head h2 {
    margin: 0;
    font-size: 42px;
    font-weight: 620;
    line-height: 1.12;
  }

  .section-more {
    display: inline-flex;
    flex: 0 0 auto;
    gap: 8px;
    align-items: center;
    min-height: 44px;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.5;
    color: var(--more);
    white-space: nowrap;
    cursor: pointer;
  }

  .section-more:hover {
    color: var(--accent);
  }

  .section-more i {
    width: 16px;
    height: 16px;
    transition: transform var(--fast) var(--ease);
  }

  .section-more:hover i {
    transform: translateX(4px);
  }

  .news-grid {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
    gap: 16px;
    min-width: 0;
    height: 403px;
  }

  .news-grid.has-latest {
    grid-template-columns: minmax(0, 2fr) minmax(0, 1.5fr) minmax(0, 1.5fr);
  }

  .news-feature-column,
  .news-brief-column,
  .news-latest-column {
    min-width: 0;
  }

  .featured-news {
    position: relative;
    display: block;
    min-width: 0;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    color: var(--white);
    background: var(--surface);
    border-radius: var(--radius-md);
  }

  .featured-link {
    position: absolute;
    inset: 0;
    display: block;
    color: inherit;
    cursor: pointer;
  }

  .featured-slide-enter-active,
  .featured-slide-leave-active {
    transition:
      opacity var(--normal) var(--ease),
      transform var(--normal) var(--ease);
  }

  .featured-slide-enter-from {
    opacity: 0;
    transform: translateX(18px);
  }

  .featured-slide-leave-to {
    opacity: 0;
    transform: translateX(-18px);
  }

  .featured-media {
    position: absolute;
    inset: 0;
  }

  .featured-media :deep(.home-media) {
    transition: transform var(--normal) var(--ease);
  }

  .featured-news:hover .featured-media :deep(.home-media) {
    transform: scale(1.025);
  }

  .featured-copy {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1;
    padding: 64px 22px 20px;
    background: linear-gradient(180deg, rgb(0 0 0 / 0%) 0%, rgb(0 0 0 / 82%) 100%);
  }

  .featured-copy h3 {
    display: -webkit-box;
    padding-right: 80px;
    margin: 0;
    overflow: hidden;
    -webkit-line-clamp: 2;
    font-size: 18px;
    font-weight: 620;
    line-height: 1.45;
    -webkit-box-orient: vertical;
  }

  .featured-indicators {
    position: absolute;
    right: 20px;
    bottom: 20px;
    z-index: 2;
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .featured-indicator {
    display: block;
    width: 8px;
    height: 8px;
    padding: 0;
    cursor: pointer;
    background: rgb(255 255 255 / 58%);
    border: 0;
    border-radius: 50%;
  }

  .featured-indicator.active {
    background: var(--accent);
  }

  .featured-indicator:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 3px;
  }

  .featured-empty {
    display: grid;
    place-items: center;
    height: 100%;
    min-height: 0;
    font-size: 14px;
    color: var(--muted);
    background: var(--surface);
    border-radius: var(--radius-md);
  }

  .news-brief-column,
  .news-latest-column {
    display: grid;
    grid-template-rows: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .brief-card {
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding: 22px 28px 18px;
    color: var(--ink);
    cursor: pointer;
    background: var(--surface);
    border-radius: var(--radius-md);
    transition:
      color var(--fast) var(--ease),
      background var(--fast) var(--ease);
  }

  .brief-card:hover {
    color: var(--ink);
  }

  .brief-card-copy {
    min-width: 0;
  }

  .brief-card h3 {
    display: -webkit-box;
    margin: 0;
    overflow: hidden;
    -webkit-line-clamp: 2;
    font-size: 16px;
    font-weight: 620;
    line-height: 1.45;
    transition: color var(--fast) var(--ease);
    -webkit-box-orient: vertical;
  }

  .brief-card:hover h3,
  .brief-card:hover .brief-card-footer i {
    color: var(--accent);
  }

  .brief-card p {
    display: -webkit-box;
    margin: 10px 0 0;
    overflow: hidden;
    -webkit-line-clamp: 2;
    font-size: 13px;
    line-height: 1.55;
    color: var(--muted);
    -webkit-box-orient: vertical;
  }

  .brief-card-footer {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    padding-top: 12px;
    margin-top: auto;
    border-top: 1px solid var(--line);
  }

  .brief-card-footer time {
    display: flex;
    flex-direction: column;
    font-family: var(--num);
    font-variant-numeric: tabular-nums;
    color: var(--muted);
  }

  .brief-card-footer time span:first-child {
    font-size: 24px;
    line-height: 1;
  }

  .brief-card-footer time span:last-child {
    margin-top: 4px;
    font-size: 12px;
    line-height: 1.2;
  }

  .brief-card-footer i {
    width: 20px;
    height: 20px;
    color: var(--muted);
    transition: transform var(--fast) var(--ease);
  }

  .brief-card:hover .brief-card-footer i {
    transform: translateX(4px);
  }

  .news-latest-column {
    grid-template-rows: repeat(3, minmax(0, 1fr));
  }

  .news-latest-column.latest-count-two {
    grid-template-rows: repeat(2, minmax(0, 1fr));
  }

  .news-list-empty {
    display: grid;
    place-items: center;
    min-height: 0;
    font-size: 14px;
    color: var(--muted);
    background: var(--surface);
    border-radius: var(--radius-md);
  }

  .news-brief-column > .news-list-empty,
  .news-latest-column > .news-list-empty {
    grid-row: 1 / -1;
  }

  .news-empty {
    min-height: 360px;
    background: var(--surface);
    border-radius: var(--radius-md);
  }

  @media (max-width: 767px) {
    .section-heading-row {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 14px 16px;
      align-items: center;
      margin-bottom: 36px;
    }

    .section-head {
      display: contents;
    }

    .section-kicker {
      grid-row: 1;
      grid-column: 1;
      min-width: 0;
      margin-bottom: 0;
    }

    .section-head h2 {
      grid-row: 2;
      grid-column: 1 / -1;
      font-size: 30px;
    }

    .section-more {
      grid-row: 1;
      grid-column: 2;
      align-self: center;
      margin: 0;
    }

    .news-grid,
    .news-grid.has-latest {
      grid-template-columns: minmax(0, 1fr);
      gap: 12px;
      height: auto;
    }

    .featured-news {
      height: auto;
      min-height: 280px;
      aspect-ratio: 1.15 / 1;
    }

    .featured-empty {
      height: auto;
      min-height: 320px;
    }

    .featured-copy {
      padding: 56px 18px 18px;
    }

    .featured-indicators {
      right: 18px;
      bottom: 18px;
    }

    .news-brief-column,
    .news-latest-column {
      gap: 12px;
    }
  }
</style>
